import { useRef, useState, type ReactNode } from 'react'
import { Item } from '@react-stately/collections'
import { OverlayContainer, useOverlayPosition, useOverlayTrigger } from '@react-aria/overlays'
import { useOverlayTriggerState } from '@react-stately/overlays'
import { ComboBox } from 'components/ComboBox/ComboBox'
import { Popover } from 'components/Popover/Popover'
import Avatar from 'common/Avatar/Avatar'
import { useDebounce } from 'hooks/useDebounce'
import { useSearchUsers } from 'hooks/queries/externalIntegrations/useSearchUsers'
import { PostTag } from 'types'
import { getInitials } from 'utils'
import Styles from './EditCaptionModal.module.scss'
import { UserProfileAdd2 } from 'blaze-ui/icons'
import X02 from 'blaze-ui/icons/16/X02'
import { Button } from 'blaze-ui/Button'

const DEFAULT_TAG_POSITION = 0.5
const VISIBLE_TAG_LIMIT = 2

export const MAX_INSTAGRAM_TAGS = 20
export const MAX_X_TAGS = 10

interface TagPickerProps {
  integrationId: number
  tags: PostTag[]
  onTagsChange: (tags: PostTag[]) => void
  disabled?: boolean
  maxTags?: number
}

export const TagPicker = ({ integrationId, tags, onTagsChange, disabled, maxTags }: TagPickerProps) => {
  const [query, setQuery] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const seeMoreTriggerRef = useRef<HTMLButtonElement>(null)
  const seeMoreOverlayRef = useRef<HTMLDivElement>(null)
  const seeMoreState = useOverlayTriggerState({ defaultOpen: false })
  const { overlayProps: seeMoreTriggerProps } = useOverlayTrigger({ type: 'dialog' }, seeMoreState, seeMoreTriggerRef)
  const { overlayProps: seeMorePositionProps } = useOverlayPosition({
    targetRef: seeMoreTriggerRef,
    overlayRef: seeMoreOverlayRef,
    placement: 'bottom start',
    offset: 4,
    isOpen: seeMoreState.isOpen,
  })
  const seeMorePositionStyle = seeMorePositionProps.style ? { ...seeMorePositionProps.style, zIndex: 1000 } : undefined
  const sanitizedQuery = query.replace(/^@/, '')
  const debouncedQuery = useDebounce(sanitizedQuery, 300)
  const { data: users = [], isFetching, isError, isAuthError } = useSearchUsers({
    integrationId,
    query: debouncedQuery,
  })

  const taggedIds = new Set(tags.map((t) => t.id))
  const availableUsers = users.filter((user) => !taggedIds.has(user.id))

  const searchActive = sanitizedQuery.length >= 3
  const isDebouncing = sanitizedQuery !== debouncedQuery
  // Use `users`, not `availableUsers`, so we don't flash "Searching..." when all matches are already tagged.
  const showSearching = searchActive && !isError && (isDebouncing || (isFetching && users.length === 0))

  let noResultsMessage: ReactNode | undefined
  if (isAuthError && searchActive) {
    noResultsMessage = (
      <div className={Styles.tagMessage}>Account disconnected. Please reconnect to search users.</div>
    )
  } else if (isError && searchActive) {
    noResultsMessage = <div className={Styles.tagMessage}>Unable to search users. Please try again.</div>
  } else if (showSearching) {
    noResultsMessage = <div className={Styles.tagMessage}>Searching...</div>
  } else if (sanitizedQuery.length >= 2) {
    noResultsMessage = <div className={Styles.tagMessage}>No users found</div>
  }

  const handleInputChange = (value: string) => {
    setQuery(value?.toLowerCase())
  }

  const hasTags = tags.length > 0
  const atLimit = maxTags != null && tags.length >= maxTags

  const getAddControl = () => {
    if (atLimit) return null

    if (isAdding) {
      return (
        <ComboBox
          aria-label="Search users to tag"
          autoFocus
          placeholder="@ Search users..."
          wrapperClassName={Styles.tagComboBoxWrapper}
          inputValue={query}
          onInputChange={handleInputChange}
          onSelectionChange={(key) => {
            const selected = users.find((u) => u.id === key)
            if (selected) {
              const nextTags = [...tags, { ...selected, x: DEFAULT_TAG_POSITION, y: DEFAULT_TAG_POSITION }]
              onTagsChange(nextTags)
              if (maxTags != null && nextTags.length >= maxTags) {
                setIsAdding(false)
                setQuery('')
              } else {
                setQuery('@')
              }
            }
          }}
          onBlur={() => {
            setIsAdding(false)
            setQuery('')
          }}
          defaultFilter={() => true}
          size="sm"
          contentsClassName={Styles.tagComboBox}
          chevronSize={0}
          popoverClassName={Styles.tagPopover}
          popoverMaxHeight={220}
          isVirtualized={false}
          disabled={disabled}
          inputClassName={Styles.tagInput}
          items={availableUsers.map((user) => ({
            id: user.id,
            name: `@${user.username}`,
            displayName: user.name,
            profile_image_url: user.profile_image_url,
          }))}
          noResultsMessage={noResultsMessage}
        >
          {(item) => (
            <Item textValue={item.name}>
              <div className={Styles.tagOption}>
                <Avatar
                  size={16}
                  label={getInitials(item.displayName || item.name)}
                  url={item.profile_image_url || null}
                />
                <div className={Styles.tagOptionText}>
                  <span className={Styles.tagOptionUsername}>{item.name}</span>
                  {item.displayName && <span className={Styles.tagOptionName}>{item.displayName}</span>}
                </div>
              </div>
            </Item>
          )}
        </ComboBox>
      )
    }

    return (
      <Button
        frontIcon={UserProfileAdd2}
        variant="secondary"
        size="md"
        square={hasTags}
        onPress={() => {
          setQuery('@')
          setIsAdding(true)
        }}
        isDisabled={disabled}
        className={Styles.tagUserButton}
        aria-label="Tag User"
      >
        {hasTags ? undefined : 'Tag User'}
      </Button>
    )
  }

  const addControl = getAddControl()

  const hiddenCount = tags.length - VISIBLE_TAG_LIMIT
  const visibleTags = tags.slice(0, VISIBLE_TAG_LIMIT)
  const overflowTags = tags.slice(VISIBLE_TAG_LIMIT)

  const renderTagPill = (tag: PostTag) => (
    <div key={tag.id} className={Styles.tagPill}>
      <Avatar size={16} label={getInitials(tag.name || tag.username)} url={tag.profile_image_url || null} />
      <span className={Styles.tagUsername}>@{tag.username}</span>
      <button
        className={Styles.tagRemove}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onTagsChange(tags.filter((t) => t.id !== tag.id))}
        disabled={disabled}
        aria-label={`Remove ${tag.username}`}
        type="button"
      >
        <X02 size={12} aria-hidden />
      </button>
    </div>
  )

  return (
    <div className={Styles.tagPickerContainer}>
      {hasTags ? (
        <div className={Styles.tagList}>
          {visibleTags.map(renderTagPill)}
          {hiddenCount > 0 && (
            <>
              <Button ref={seeMoreTriggerRef} variant="secondary" size="md" onPress={seeMoreState.toggle}>
                +{hiddenCount} more
              </Button>
              {seeMoreState.isOpen && (
                <OverlayContainer>
                  <Popover
                    {...seeMoreTriggerProps}
                    {...seeMorePositionProps}
                    style={seeMorePositionStyle}
                    isOpen={seeMoreState.isOpen}
                    onClose={seeMoreState.close}
                    ref={seeMoreOverlayRef}
                    isDismissable
                    backdropClass={Styles.tagOverflowBackdrop}
                    className={Styles.tagOverflowPopover}
                  >
                    <div className={Styles.tagOverflowList}>{overflowTags.map(renderTagPill)}</div>
                  </Popover>
                </OverlayContainer>
              )}
            </>
          )}
          {addControl}
        </div>
      ) : (
        addControl
      )}
    </div>
  )
}
